import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toastWriteFailure } from "./writeFailure";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { EntityKind } from "../Dashboard/types";
import { updateTransactionCells } from "../Dashboard/api";
import {
  keyForSource,
  useRecordDataSource,
} from "../Dashboard/recordDataSource";

/** How a column renders. Mirrors `transaction_column_data_type` (#10922). */
export type TransactionFieldDataType =
  | "string"
  | "number"
  | "percentage"
  | "date"
  | "boolean";

// The friendly names each type is shown under now live in
// `Dashboard/Components/columnDataType.ts`, shared with Holdings so the two
// tables cannot name the same type differently (#11036).

// No protected-column list and no built-in label/type tables live here any more.
// Nothing is un-deletable, and which built-in a column is bound to is decided by
// the backend from the column's name when it is created — so the frontend has no
// reason to hold a second copy of that vocabulary, which could only drift.

/** One column in the organization's own layout (#10922). */
export interface TransactionColumn {
  id: number;
  key: string;
  name: string;
  position: number;
  data_type: TransactionFieldDataType;
  description: string | null;
}

/** Filter + sort arguments for the ledger. All applied server-side.
 *
 * The per-source filters this used to carry — a fund id, a portfolio id, a
 * transaction type — are gone (#10997). Anything worth filtering on is a column
 * the organization defined, reached through the grid's own column filters.
 */
export interface LedgerQuery {
  sortBy?: string;
  sortDir?: "asc" | "desc";
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

/** One ledger line: the row's own fields, plus the org's own columns. */
export interface LedgerLine {
  date: string;
  /** Signed as the source reported it — money returned is negative. */
  amount: number;
  /** The entity this row is attached to, or null. */
  entity: { id: string; kind: EntityKind; name: string } | null;
  /** Every column the organization configured, keyed by its column key. */
  values: Record<string, unknown>;
}

/** What a set of ledger lines adds up to.
 *
 * Replaces the per-category totals strip: a net figure is the only total that
 * can be computed without the application imposing a category taxonomy.
 */
export interface LedgerTotal {
  total: number;
  line_count: number;
}

export interface TransactionsResponse {
  totals: LedgerTotal;
  lines: LedgerLine[];
  /** Retained name; now means "another page follows". */
  truncated: boolean;
  has_more: boolean;
  offset: number;
}

/** Only the params the caller actually set reach the URL. */
function ledgerParams(query: LedgerQuery): string {
  const params = new URLSearchParams();
  if (query.sortBy) params.set("sort_by", query.sortBy);
  if (query.sortDir) params.set("sort_dir", query.sortDir);
  if (query.dateFrom) params.set("date_from", query.dateFrom);
  if (query.dateTo) params.set("date_to", query.dateTo);
  if (query.q) params.set("q", query.q);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

export async function fetchTransactions(
  entityKind: EntityKind,
  entityId: string,
  query: LedgerQuery
): Promise<TransactionsResponse> {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/entities/${entityKind}/${entityId}/transactions${ledgerParams(query)}`
  );
  if (!response.ok) throw new Error("Failed to fetch transactions");
  return response.json();
}

export function useTransactions(
  entityKind: EntityKind,
  entityId: string,
  query: LedgerQuery = {}
) {
  // Read through the record's data source (#13370, round 36 review): Appearance's demo record
  // answers from its own rows.
  const source = useRecordDataSource();
  return useQuery({
    // The query is part of the key: filtering and sorting happen on the server,
    // so a different query is a different result set, not a client-side re-sort.
    queryKey: keyForSource(source, [
      "rms",
      "transactions",
      entityKind,
      entityId,
      query,
    ]),
    queryFn: () => source.transactions(entityKind, entityId, query),
    enabled: !!entityId,
  });
}

// The organization-wide ledger no longer has a bespoke fetcher: the global
// Transactions view renders through the shared RMS grid, which reads
// POST /rms/transactions/search like every other non-entity source
// (#10760 slice 6). What lives on here is the per-entity tab plus the column
// configuration the two surfaces share.

const TRANSACTION_KEYS = {
  columns: (organizationId?: string) =>
    ["rms", "transactions", "columns", organizationId] as const,
};

function orgSuffix(organizationId?: string): string {
  return organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : "";
}

function columnsUrl(organizationId?: string): string {
  return `${BASE_API_URL_V2}/api/v2/rms/transactions/columns${orgSuffix(
    organizationId
  )}`;
}

async function fetchTransactionColumns(
  organizationId?: string
): Promise<TransactionColumn[]> {
  const response = await fpFetch(columnsUrl(organizationId));
  if (!response.ok) throw new Error("Failed to fetch transaction columns");
  return response.json();
}

export function useTransactionColumns(organizationId?: string) {
  return useQuery({
    queryKey: TRANSACTION_KEYS.columns(organizationId),
    queryFn: () => fetchTransactionColumns(organizationId),
  });
}

/**
 * One column as the editor sends it.
 *
 * `id` is null for a column just created; sending back the id a column came
 * with is what keeps its values attached through a rename.
 */
export interface TransactionColumnInput {
  id: number | null;
  key: string;
  name: string;
  data_type: TransactionFieldDataType;
  description?: string | null;
}

/** Turn a saved column back into the shape the editor sends. */
export function toColumnInput(
  column: TransactionColumn
): TransactionColumnInput {
  return {
    // A column that has never been saved arrives with id 0 (the fallback
    // layout); send null so the backend creates it rather than looking for it.
    id: column.id > 0 ? column.id : null,
    key: column.key,
    name: column.name,
    data_type: column.data_type,
    description: column.description,
  };
}

export function useUpdateTransactionColumns() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      columns,
      organizationId,
    }: {
      columns: TransactionColumnInput[];
      organizationId?: string;
    }) => {
      const response = await fpFetch(columnsUrl(organizationId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ columns }),
      });
      if (!response.ok) {
        // The backend refuses unknown field keys with a readable message; show
        // it rather than a generic failure.
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail ?? "Failed to save transaction columns");
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: TRANSACTION_KEYS.columns(variables.organizationId),
      });
      // The ledger selects whatever the layout names, so its rows change too.
      queryClient.invalidateQueries({
        queryKey: ["rms", "transactions"],
      });
      // The shared grid reads the transactions schema — and each fact's
      // position, which IS the configured layout — out of the RMS *bootstrap*
      // for the current org, not the standalone schema query. Invalidating the
      // bootstrap is what makes the grid re-fetch and re-derive its visible
      // columns, so a save in this editor actually moves the table rather than
      // only the RMS-Setup preview (#10760 slice 13). The schema key is
      // invalidated too for the admin/override path that uses it.
      queryClient.invalidateQueries({ queryKey: ["rms", "bootstrap"] });
      queryClient.invalidateQueries({ queryKey: ["rms", "schema"] });
    },
  });
}

// ── Manual transaction write path (#10904) ───────────────────────────────────
//
// A user can add a transaction the way they add Holdings; it lands as a
// source='manual' row in the same ledger the connectors feed. Edit and delete
// hit only manual rows (the backend guards on source), so connector-synced data
// is never mutated from the UI.

/** One manual transaction as the write routes return it. */
export interface ManualTransaction {
  id: number;
  source: string;
  entity_id: number | null;
  date: string;
  amount: number | null;
  currency: string | null;
  description: string | null;
  created_by_user: string | null;
  /** The organization's own column values, keyed by column name — what the edit
   * form seeds its inputs from, since the form is the schema. */
  values?: Record<string, unknown>;
}

/** One value for one of the organization's own columns (#10922). */
export interface TransactionCellInput {
  column_id: number;
  value: unknown;
  source_document_id?: string | null;
  source_page_num?: number | null;
}

export interface CreateTransactionPayload {
  entity_public_id: string;
  date: string;
  /** Optional: the amount is an ordinary schema column (#10997), so the create
   * form does not ask for one outside the schema. Still accepted, because a
   * connector writes it on every row it lands. Signed as stored — distributions
   * and fees are negative. */
  amount?: number | null;
  currency?: string | null;
  description?: string | null;
  /** Values for the org's own columns — which is everything the form collects
   * apart from the batch date and the entity. */
  cells?: TransactionCellInput[];
}

/** A partial update of a manual transaction (#14991): the server keeps
 *  every field the body omits, so the tab's date edit sends `date` alone. */
export interface EditTransactionPayload {
  date?: string;
  amount?: number | null;
  currency?: string | null;
  description?: string | null;
  cells?: TransactionCellInput[];
}

/** What a DELETE answers (#14991): the removed row with its cells in the
 *  create route's own shape (citations included), enough for Undo to post
 *  it straight back. */
export interface RemovedTransaction {
  id: number;
  date: string;
  amount: number | null;
  currency: string | null;
  description: string | null;
  cells: TransactionCellInput[];
}

async function readError(response: Response, fallback: string): Promise<never> {
  // The backend refuses bad input with a readable `detail`; surface it.
  const detail = await response.json().catch(() => null);
  throw new Error(detail?.detail ?? fallback);
}

export async function createTransaction(
  payload: CreateTransactionPayload
): Promise<ManualTransaction> {
  const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/rms/transactions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) return readError(response, "Failed to add transaction");
  return response.json();
}

export async function updateTransaction(
  transactionId: number,
  payload: EditTransactionPayload
): Promise<ManualTransaction> {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/transactions/${transactionId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) return readError(response, "Failed to save transaction");
  return response.json();
}

export async function deleteTransaction(
  transactionId: number
): Promise<RemovedTransaction> {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/transactions/${transactionId}`,
    { method: "DELETE" }
  );
  if (!response.ok) return readError(response, "Failed to delete transaction");
  return response.json();
}

/** Invalidate the grid, its footer total, the totals strip, and the
 * per-entity ledger — a write to one manual row changes all four (#14991). */
function invalidateTransactionViews(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "rms" &&
      (q.queryKey[1] === "search" ||
        q.queryKey[1] === "count" ||
        q.queryKey[1] === "transactions"),
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransactionPayload) =>
      createTransaction(payload),
    onSuccess: () => invalidateTransactionViews(queryClient),
    onError: toastWriteFailure,
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      payload,
    }: {
      transactionId: number;
      payload: EditTransactionPayload;
    }) => updateTransaction(transactionId, payload),
    onSuccess: () => invalidateTransactionViews(queryClient),
    onError: toastWriteFailure,
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: number) => deleteTransaction(transactionId),
    onSuccess: () => invalidateTransactionViews(queryClient),
    onError: toastWriteFailure,
  });
}

/** A row's configured cells as the phone sheet writes them. */
export function useUpdateTransactionCells() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      cells,
    }: {
      transactionId: number;
      cells: { column_id: number; value: unknown }[];
    }) => updateTransactionCells(String(transactionId), cells),
    onSuccess: () => invalidateTransactionViews(queryClient),
    onError: toastWriteFailure,
  });
}
